import React from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import Translate from '@utils/translation-functions';
import { ArrowRightIconAtom } from '../../icon-atoms/icon-atoms';
import { GTM_ID } from '../../../../../common/constants/gtmIds';
import { Link } from 'gatsby';
import { NavbarContentLinkProps } from './navbar-content-link.types';
import { WrapperProps } from '../navbar-atoms-common.types';

const sharedWrapperStyles = `
  display: flex;
  flex-direction: row;
  margin-bottom: ${vars.spacingM};
  text-decoration: none;
  border-bottom: none;
  align-items: center;  
  padding: ${vars.spacingS};
  padding-left: 0;
  width: 100%;

  &:hover .readmore::before {
    transform: scaleX(1);
  }

  @media screen and (min-width: ${vars.breakPointTablet}) {
    flex-direction: column;
    width: calc(100% / 3);

    &:first-of-type {
      margin-top: 0;
      padding-left: 0;
    }
  }

  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {    
    flex-direction: row;
    width: 100%;
    padding: 0;
    &:first-of-type {
      margin-top: ${vars.spacingS};
    }
  }
`;

const StyledExternalWrapper = styled.a`
  ${sharedWrapperStyles}
`;

const StyledWrapper = styled(Link)`
  ${sharedWrapperStyles}
`;

const Image = styled.img`
  border-radius: 5px;
  max-width: 16rem;
  margin-right: ${vars.spacingM};
  margin-bottom: 0;

  @media screen and (min-width: ${vars.breakPointTablet}) {
    max-width: 100%;
    margin-bottom: ${vars.spacingS};
    margin-right: 0;
  }

  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
    max-width: 16rem;
    margin-right: ${vars.spacingM};
  }
`;
const Title = styled.p`
  font-weight: ${vars.fontWeightMedium};
  font-size: 1.6rem;
  margin-right: 0px;
  padding: 0px;
  padding-top: 0.2rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMore = styled.p`
  position: relative;
  display: inline-block;
  line-height: 2rem;
  font-weight: ${vars.fontWeightMedium};
  font-size: 1.6rem;
  text-decoration-thickness: 3px;
  margin-right: ${vars.spacingXs};

  &:before {
    content: '';
    position: absolute;
    display: block;
    width: 100%;
    height: 1px;
    bottom: 0;
    left: 0;
    background-color: #000;
    transform: scaleX(0);
    transform-origin: top left;
    transition: transform 0.3s ease;
  }
`;

const ReadMoreWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${vars.spacingS};
`;

const Wrapper: React.FC<WrapperProps> = ({
  isExternal,
  children,
  link,
  gtmId,
  handleReset,
}) => {
  if (isExternal) {
    return (
      <StyledExternalWrapper
        href={link}
        data-gtm-id={gtmId}
        rel="noreferrer noopener"
        target="_blank"
        onClick={handleReset}
      >
        {children}
      </StyledExternalWrapper>
    );
  }
  return (
    <StyledWrapper to={link || ''} data-gtm-id={gtmId} onClick={handleReset}>
      {children}
    </StyledWrapper>
  );
};

export const NavbarContentLink: React.FC<NavbarContentLinkProps> = ({
  item,
  columnNumber,
  handleReset,
  translate = true,
}) => {
  return (
    <Wrapper
      link={item.linkPath}
      gtmId={`${GTM_ID.FLYOVER_LINK_COLUMN}${columnNumber}`}
      isExternal={item.isExternalLink}
      handleReset={handleReset}
    >
      {item.image && (
        <Image
          data-testid="image"
          src={item?.image?.file?.url}
          alt={item.image.description}
        />
      )}
      <div>
        <Title>{item.title}</Title>
        <ReadMoreWrapper>
          <ReadMore className="readmore">
            {Translate('Read more', translate)}
          </ReadMore>
          <ArrowRightIconAtom />
        </ReadMoreWrapper>
      </div>
    </Wrapper>
  );
};
